import React, {
	memo,
	createContext,
	useContext,
	useState,
	useCallback,
	type ReactNode,
	type Dispatch,
	type SetStateAction,
} from 'react';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common';
import {
	GqlsRealtime,
	type GqlsRealtimeEvent,
	isIssueEvent,
	ISSUE_CREATED_EVENT,
} from '@atlassian/jira-business-gqls-realtime';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers';
import type { TimelineViewIssuesReturn } from '../../services/get-timeline-view-issues/types';
import { useTimelineViewUpdater as useTimelineViewUpdaterService } from '../../services/timeline-view-updater';

const LOCK_DELAY = 2000;
export const REALTIME_APP_ID = 'jira-business-timeline-view';

type TimeoutId = ReturnType<typeof setTimeout>;
type IssueLockMap = Map<number, TimeoutId>;

type ContextType = [
	lockedIssuesMap: IssueLockMap,
	setLockedIssueMap: Dispatch<SetStateAction<IssueLockMap>>,
];

type ProviderProps = {
	children: ReactNode;
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
const Context = createContext<ContextType>(null as any);

export const TimelineViewUpdaterProvider = ({ children }: ProviderProps) => {
	const value: ContextType = useState<IssueLockMap>(new Map());

	return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useLockIssue = () => {
	const [, setLockedIssueMap] = useContext(Context);

	return useCallback(
		(issueId: number) => {
			setLockedIssueMap((prev) => {
				// if a timeout is already registered for that issue, clear it first
				const existingTimeoutId = prev.get(issueId);
				if (existingTimeoutId != null) {
					clearTimeout(existingTimeoutId);
				}

				// unlock issue after a given delay
				const timeoutId = setTimeout(() => {
					setLockedIssueMap((prev2) => {
						const newMap = new Map(prev2);
						newMap.delete(issueId);
						return newMap;
					});
				}, LOCK_DELAY);

				return new Map(prev).set(issueId, timeoutId);
			});
		},
		[setLockedIssueMap],
	);
};

type Props = {
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
};

export const TimelineViewUpdater = memo(({ childrenQueryData }: Props) => {
	const { data: entity } = useBusinessEntity();
	const [lockedIssuesMap] = useContext(Context);
	const { onIssueUpdated, onIssueCreated } = useTimelineViewUpdaterService({
		childrenQueryData,
	});

	const onRealtimeEvent = (event: GqlsRealtimeEvent) => {
		if (isIssueEvent(event)) {
			const issueId = Number(event.payload.issueId);
			if (lockedIssuesMap.has(issueId)) {
				return;
			}

			if (event.type === ISSUE_CREATED_EVENT) {
				onIssueCreated([issueId]);
			} else {
				onIssueUpdated([issueId]);
			}
		}
	};

	return <GqlsRealtime appId={REALTIME_APP_ID} entityId={entity.id} onReceive={onRealtimeEvent} />;
});
